import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { apiPut, apiPost } from '../../utils/apiFetch';
import apiPath from '../../utils/pathObj';
import useToastContext from 'hooks/useToastContext';
import ErrorMessage from 'components/ErrorMessage';
import ODateTimePicker from 'components/shared/datePicker/ODateTimePicker';

const ScheduleZoomInterview = ({ 
  setJobRequest, 
  setPage, 
  allRequest, 
  currentItem, 
  jobApply,
  isReschedule = false 
}) => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const [interviewDateTime, setInterviewDateTime] = useState(new Date());
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [zoomLink, setZoomLink] = useState('');
  const [zoomMeetingId, setZoomMeetingId] = useState('');
  const [zoomPassword, setZoomPassword] = useState('');
  const [zoom, setZoom] = useState({});
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDateTimeChange = (dateTime) => {
    setInterviewDateTime(dateTime);
  };

  const generateZoomLink = async () => {
    setIsGeneratingLink(true);
    
    try {
      const formattedDate = interviewDateTime.toISOString().split('.')[0] + 'Z';
      
      const response = await apiPost(apiPath.getZoomLink, {
        topic: `Interview with ${currentItem?.Employee?.name} for ${currentItem?.Job?.name}`,
        start_time: formattedDate,
        duration: 60,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        agenda: `Interview discussion for ${currentItem?.Job?.name} position`
      });
      
      if (response.data.success) {
        setZoomLink(response.data.meeting.join_url);
        setZoomMeetingId(response.data.meeting.id);
        setZoomPassword(response.data.meeting.password);
        setZoom(response.data.meeting);
        notification.success('Zoom meeting created successfully');
      } else {
        notification.error(response.message || 'Failed to create Zoom meeting');
      }
    } catch (error) {
      console.error('Error creating Zoom meeting:', error);
      notification.error('Failed to create Zoom meeting. Please try again.');
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const handleSchedule = async () => {
    // Validate inputs
    if (!interviewDateTime) {
      notification.error('Please select an interview date and time');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // If no zoom link exists, generate one
      if (!zoomLink) {
        await generateZoomLink();
        await new Promise(resolve => setTimeout(resolve, 100));
        
        if (!zoomLink) {
          notification.error('Failed to generate Zoom meeting link');
          setIsSubmitting(false);
          return;
        }
      }

      // Using existing interview-schedule endpoint with interviewType='zoom'
      const payload = {
        jobID: jobApply._id || currentItem._id,
        interviewDate: interviewDateTime.toISOString(),
        interviewMeetingData: zoom,
        interviewStatus: isReschedule ? 'rescheduled' : 'scheduled',
        notes: notes,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        interviewType: 'zoom' // This tells the backend to use employeeRequest.interviewSchedule
      };

      const res = await apiPut(
        apiPath.sendInterviewSchedule, // Using existing endpoint
        payload
      );
      
      if (res?.data?.success) {
        allRequest();
        setPage(1);
        setJobRequest(false);
        notification.success(res?.data?.message || 'Interview scheduled successfully');
      } else {
        notification.error(res?.data?.message || 'Failed to schedule interview');
      }
    } catch (err) {
      console.log('err:', err);
      notification.error('An error occurred while scheduling the interview');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setJobRequest(false);
  };

  return (
    <>
      <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
        <div className='relative w-auto my-6 mx-auto max-w-3xl'>
          <div className='sm:py-4 sm:px-2 py-8 px-7'>
            <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none min-w-[500px]'>
              <div className='flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t'>
                <h3 className='text-xl font-semibold'>
                  {isReschedule ? t('RESCHEDULE_ZOOM_INTERVIEW') : t('SCHEDULE_ZOOM_INTERVIEW')}
                </h3>
                <button
                  className='ml-auto flex items-center justify-center text-black border-2 rounded-full h-8 w-8 float-right text-3xl leading-none font-extralight outline-none focus:outline-none'
                  onClick={handleClose}
                  type="button"
                >
                  <span className='text-[#B8BBBF] text-4xl' title='Close'>
                    ×
                  </span>
                </button>
              </div>
              <div className='relative p-6 flex-auto'>
                <div className='grid sm:grid-cols-1 gap-4'>
                  {/* Interview Date and Time Picker */}
                  <div className='w-full'>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      {t('INTERVIEW_DATE_TIME')}
                      <span className='text-red-500'>*</span>
                    </label>
                    <div className="relative">
                      <ODateTimePicker 
                        handleDateTimeChange={handleDateTimeChange} 
                        value={interviewDateTime}
                      />
                    </div>
                    {!interviewDateTime && (
                      <ErrorMessage message='Please select interview date and time' />
                    )}
                  </div>

                  {/* Zoom Link Section */}
                  <div className='w-full'>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      {t('ZOOM_MEETING_LINK')}
                    </label>
                    <div className="flex items-center">
                      <input
                        type="text"
                        value={zoomLink}
                        readOnly
                        placeholder="Click generate to create a meeting link"
                        className="block w-full p-3 text-sm text-gray-900 border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0 bg-gray-100"
                      />
                      <button
                        type="button"
                        onClick={generateZoomLink}
                        disabled={isGeneratingLink || isSubmitting}
                        className="ml-2 bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {isGeneratingLink ? 'Generating...' : 'Generate Link'}
                      </button>
                    </div>
                    
                    {zoomMeetingId && (
                      <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                        <p className="text-xs text-gray-600">
                          <strong>Meeting ID:</strong> {zoomMeetingId}
                          {zoomPassword && <span className="ml-3"><strong>Password:</strong> {zoomPassword}</span>}
                        </p>
                      </div>
                    )}
                    
                    <p className="text-xs text-gray-500 mt-1">
                      A secure Zoom link will be automatically generated for the interview
                    </p>
                  </div>

                  {/* Additional Notes (Optional) */}
                  <div className='w-full'>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      {t('ADDITIONAL_NOTES')} ({t('OPTIONAL')})
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder={t('ADDITIONAL_INSTRUCTIONS')}
                      className="block w-full p-3 text-sm text-gray-900 border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0"
                      rows="3"
                    />
                  </div>
                </div>
              </div>
              <div className='flex items-center justify-center p-6 border-t border-solid border-slate-200 rounded-b gap-4'>
                <button
                  className='bg-blue-500 text-white active:bg-blue-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none disabled:opacity-50'
                  type='button'
                  onClick={handleSchedule}
                  disabled={!zoomLink || !interviewDateTime || isSubmitting}
                >
                  {isSubmitting ? 'Scheduling...' : isReschedule ? t('RESCHEDULE_INTERVIEW') : t('SCHEDULE_INTERVIEW')}
                </button>
                
                <button
                  className='bg-gray-200 text-gray-700 active:bg-gray-300 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150'
                  type='button'
                  onClick={handleClose}
                >
                  {t('CANCEL')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='opacity-25 fixed inset-0 z-40 bg-black' />
    </>
  );
};

export default ScheduleZoomInterview;